import { Txn } from '../money.model';
import { spendableTxns, monthKey } from '../dashboard/dashboard.aggregate';

export type Cadence = 'weekly' | 'biweekly' | 'monthly' | 'annual';
export type Direction = 'expense' | 'income';
/** 'fixed' when the amount holds steady; 'variable' when timing is regular but the amount is not (e.g. a utility bill). */
export type AmountShape = 'fixed' | 'variable';

export interface RecurringSeries {
  key: string;
  merchant: string;
  direction: Direction;
  cadence: Cadence;
  /** Positive magnitude, in cents. */
  medianCents: number;
  /** Classification, not a detection gate — see amountsAreStable. */
  amountShape: AmountShape;
  minCents: number;
  maxCents: number;
  monthlyEquivalentCents: number;
  occurrences: number;
  firstDate: string;
  lastDate: string;
  nextExpected: string;
  /** True when the series has gone quiet well beyond its own cadence — see isSeriesStale. */
  isStale: boolean;
  /** Which detection path accepted this series — see looksMonthlyByDay. */
  detectedBy: 'interval' | 'day-of-month';
  /** Set only on a series recovered by the billing-day rescue path — see splitByBillingDay. */
  billingDay?: number;
}

/** One occurrence per calendar date; same-day charges are one obligation for that day. */
export interface DailyOccurrence { date: string; amount: number; }

export const CADENCE_DAYS: Record<Cadence, number> = {
  weekly: 7, biweekly: 14, monthly: 30, annual: 365,
};

const INTERVAL_TOLERANCE = 0.15;
const AMOUNT_TOLERANCE = 0.15;
/**
 * How many cadence-lengths of silence turn a series stale. A monthly bill
 * that has not posted in over 45 days (1.5 * 30) has stopped, not merely
 * drifted — intervalsAreRegular already tolerates ordinary drift.
 */
export const STALENESS_FACTOR = 1.5;
/**
 * Shared by amountsAreStable and intervalsAreRegular: the proportion of
 * occurrences/intervals that must sit near the expected value. One idea
 * (tolerate a single outlier), not two arbitrary thresholds.
 */
const STABLE_PROPORTION = 0.8;
const MIN_OCCURRENCES = 3;
const MIN_DISTINCT_MONTHS = 3;
/** Tokens used to group variants of one merchant. */
const KEY_TOKENS = 2;
/** A product name must be this long to be treated as one rather than a reference code. */
const MIN_PRODUCT_TOKEN = 3;
const PRODUCT_TOKEN_PATTERN = new RegExp(`^[A-Za-z]{${MIN_PRODUCT_TOKEN},}$`);

/**
 * Payment-processor codes that can appear before a `*` in a card description
 * (e.g. `WL *STEAM PURCHASE`). Vocabulary borrowed from the PROCESSOR_PREFIX
 * list in csv/normalize.ts (SQ, TST, PY, VENMO, TC), plus WL (Worldline),
 * confirmed from the real ledger. Declared separately rather than imported:
 * normalize.ts governs rule-matching text cleanup, this governs recurring-key
 * grouping, and coupling the two would tie unrelated concerns together.
 */
const PROCESSOR_CODES = new Set(['SQ', 'TST', 'PY', 'VENMO', 'TC', 'WL']);

/**
 * The product segment of a `MERCHANT *PRODUCT` description, or '' if there
 * isn't one.
 *
 * Needed because Apple's Merchant column collapses distinct products to one
 * name: `GOOGLE *FIBER` (internet, $71.40 monthly) and `GOOGLE *CLOUD`
 * (metered, cents) both arrive as merchant "Google", so the merchant key
 * merged a real bill with noise.
 *
 * Purely alphabetic tokens only. Amazon's star segment is an order id
 * (`MKTPL*5A4ST18C1440`); using it would explode one merchant into a singleton
 * per transaction and destroy grouping outright.
 *
 * The star segment is only a product when the text before the star is the
 * merchant. When that text is a payment processor code instead (`WL *STEAM
 * PURCHASE` — WL is Worldline), the star segment is the merchant, not a
 * product, and must not be used: one STEAM transaction routed through
 * Worldline while another (STEAMGAMES.COM) did not, and treating "STEAM" as
 * a bogus product suffix fragmented one recurring bill into two series.
 */
export function productToken(description: string): string {
  const text = description ?? '';
  const star = text.indexOf('*');
  if (star < 0) return '';
  const token = text.slice(star + 1).trim().split(/\s+/)[0] ?? '';
  if (!PRODUCT_TOKEN_PATTERN.test(token)) return '';

  // Handles both "MERCHANT *X" and "MERCHANT* X" shapes: whichever side of
  // the star the space lands on, the preceding token is the last word before it.
  const before = text.slice(0, star).trim().split(/\s+/);
  const precedingToken = before[before.length - 1] ?? '';
  if (PROCESSOR_CODES.has(precedingToken.toUpperCase())) return '';

  return token.toUpperCase();
}

/**
 * Grouping key for one recurring series.
 *
 * Only the first two tokens are used: real card-swipe descriptions fragment by
 * store and city (WINCO FOODS appears under five distinct stems in the live
 * ledger), and those variants are one merchant.
 *
 * Direction is part of the key, not merely a filter, because the two-token
 * prefix genuinely collides across flows — VENMO TYPE PAYMENT (out) and
 * VENMO TYPE CASHOUT (in) both reduce to "VENMO TYPE".
 *
 * When description is provided, the product token (extracted from MERCHANT *PRODUCT
 * format) is appended to distinguish transactions from different products of the
 * same merchant (e.g., GOOGLE FIBER vs GOOGLE CLOUD).
 *
 * If the product token duplicates a token already in the stem (e.g. `PATREON*
 * MEMBERSHIP` has stem "PATREON MEMBERSHIP" and product token "MEMBERSHIP"),
 * it adds no distinguishing information and is dropped — otherwise the series
 * renders as "PATREON MEMBERSHIP MEMBERSHIP" on screen.
 */
export function recurringKey(
  matchKey: string, direction: Direction, description = '',
): string {
  const stem = (matchKey ?? '').trim().split(/\s+/).filter(Boolean)
    .slice(0, KEY_TOKENS).join(' ');
  const product = productToken(description);
  const redundant = !!product && stem.split(' ').includes(product);
  return `${direction}:${stem}${product && !redundant ? `|${product}` : ''}`;
}

const daysBetween = (a: string, b: string): number =>
  Math.round((Date.parse(`${b}T00:00:00`) - Date.parse(`${a}T00:00:00`)) / 86400000);

export const median = (values: number[]): number => {
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : Math.round((sorted[mid - 1] + sorted[mid]) / 2);
};

/** The cadence whose period the interval sits closest to, or null if none fits. */
export function detectCadence(intervalDays: number): Cadence | null {
  if (!(intervalDays > 0)) return null;
  const hit = (Object.keys(CADENCE_DAYS) as Cadence[]).find(c =>
    Math.abs(intervalDays - CADENCE_DAYS[c]) <= CADENCE_DAYS[c] * INTERVAL_TOLERANCE);
  return hit ?? null;
}

/** Cents per month for a charge at this cadence. */
export function monthlyEquivalent(cents: number, cadence: Cadence): number {
  switch (cadence) {
    case 'weekly':   return Math.round(cents * 4.33);
    case 'biweekly': return Math.round(cents * 2.17);
    case 'annual':   return Math.round(cents / 12);
    default:         return cents;
  }
}

/**
 * A proportion rather than a blanket rule: a utility bill that drifts, or one
 * unusual month, must still qualify, while a genuinely erratic merchant must not.
 *
 * This is a classification (see RecurringSeries.amountShape), not a detection
 * gate. A power bill can swing far more than 15% month to month and still be
 * exactly as recurring as a fixed-rent charge — see intervalsAreRegular.
 */
export function amountsAreStable(amounts: number[]): boolean {
  if (amounts.length === 0) return false;
  const med = median(amounts);
  if (med === 0) return false;
  const near = amounts.filter(a => Math.abs(a - med) <= med * AMOUNT_TOLERANCE).length;
  return near / amounts.length >= STABLE_PROPORTION;
}

/**
 * A proportion rather than a blanket rule, mirroring amountsAreStable: one
 * holiday-shifted date must not discard an otherwise-monthly series, while a
 * genuinely irregular merchant must still be rejected.
 */
export function intervalsAreRegular(intervals: number[], cadence: Cadence): boolean {
  if (intervals.length === 0) return false;
  const cadenceDays = CADENCE_DAYS[cadence];
  const tolerance = cadenceDays * INTERVAL_TOLERANCE;
  const near = intervals.filter(i => Math.abs(i - cadenceDays) <= tolerance).length;
  return near / intervals.length >= STABLE_PROPORTION;
}

/**
 * Collapses same-date occurrences before cadence math. Two charges posted on
 * one calendar date (e.g. rent plus a same-day fee) are one obligation that
 * day, not a 0-day interval that corrupts the median cadence.
 */
export function collapseSameDay(txns: Txn[]): DailyOccurrence[] {
  const byDate = new Map<string, number>();
  txns.forEach(t => byDate.set(t.date, (byDate.get(t.date) ?? 0) + t.amount));
  return [...byDate.entries()]
    .map(([date, amount]) => ({ date, amount }))
    .sort((a, b) => a.date.localeCompare(b.date));
}

/**
 * Add `days` to an ISO date, staying in local calendar arithmetic throughout.
 *
 * The old implementation parsed at local midnight, added the offset in
 * milliseconds, then read the result back through `toISOString()` — which
 * reports UTC, not local time. East of Greenwich (a positive UTC offset),
 * local midnight is already the *previous* UTC day, so that round-trip
 * silently lost a day. Using the Date object's own local getters/setters
 * throughout (same pattern as `defaultCutoffDate` in money.model.ts) never
 * crosses into UTC, so it is correct at every offset.
 */
export const addDays = (date: string, days: number): string => {
  const d = new Date(`${date}T00:00:00`);
  d.setDate(d.getDate() + days);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/**
 * A series is stale once it has gone quiet for more than STALENESS_FACTOR
 * cadence-lengths. Classification, not a filter — see detectRecurring.
 */
export function isSeriesStale(lastDate: string, cadence: Cadence, asOf: string): boolean {
  return daysBetween(lastDate, asOf) > CADENCE_DAYS[cadence] * STALENESS_FACTOR;
}

/**
 * Day-of-month spread a monthly bill may show, derived from a stated
 * principle rather than fitted to this ledger: a monthly bill's posting date
 * may wander by up to about a sixth of its cycle and still be the same bill.
 * CADENCE_DAYS.monthly / 6 = 5.0.
 *
 * That margin is not the only safety net. If a real bill ever drifts past
 * this limit it does not silently vanish — it reappears in the "Not
 * detected" candidate list (recurringCandidates in recurring.calc.ts), where
 * the user can pin it by hand. This constant only decides which of the two
 * lists a borderline bill lands in, not whether it is visible at all.
 */
export const DOM_SIGMA_MAX = CADENCE_DAYS.monthly / 6;
/** A bill arrives about once a month; a shop is visited more often. */
export const MAX_PER_MONTH = 1.15;
/** A day-of-month coincidence needs more evidence than an interval match. */
export const MIN_DOM_OCCURRENCES = 4;

/**
 * How far apart two day-of-month numbers may sit and still be the same
 * billing day. Adjacent days only: a single obligation posts on its due date
 * and slips by at most a day when that date is awkward, whereas two charges
 * separated by a clear day are two different due dates.
 *
 * The tight value is the conservative direction, not the aggressive one. This
 * split is a last-resort rescue applied only to merchants already rejected as
 * too frequent (occurrencesPerMonth > MAX_PER_MONTH) — and that is precisely
 * the condition recurringCandidates (recurring.calc.ts) also rejects on, so a
 * merchant that reaches this path is invisible today, in neither the
 * detected list nor the "Not detected" candidate list. Every partition this
 * split produces must still clear looksMonthlyByDay and amountsAreStable
 * (see detectRecurring) before it becomes a series.
 *
 * An over-tight split costs nothing: the fragments simply fail those gates
 * and the merchant stays invisible, exactly as it is today. An over-loose
 * split re-merges two subscriptions back into one too-frequent group, which
 * also stays invisible — no worse than today, just a missed opportunity
 * rather than a regression. Both failure modes land on that same
 * pre-existing invisibility, which is why the tight value is the safe
 * direction: this rescue path can only add series, never remove one.
 */
export const BILLING_DAY_DRIFT = 1;

export function dayOfMonthSigma(dates: string[]): number {
  const days = (dates ?? []).map(d => Number(d.slice(8, 10)));
  if (days.length < 2) return 0;
  const mean = days.reduce((a, b) => a + b, 0) / days.length;
  return Math.sqrt(days.reduce((a, d) => a + (d - mean) ** 2, 0) / days.length);
}

/** The display name carried by a recurring key, with the product separator flattened. */
export function merchantFromKey(key: string): string {
  return key.slice(key.indexOf(':') + 1).replace('|', ' ');
}

/** 1st, 2nd, 3rd, 4th — teens are always 'th'. */
export function ordinal(n: number): string {
  const teen = n % 100;
  if (teen >= 11 && teen <= 13) return `${n}th`;
  switch (n % 10) {
    case 1:  return `${n}st`;
    case 2:  return `${n}nd`;
    case 3:  return `${n}rd`;
    default: return `${n}th`;
  }
}

/** The representative billing day for a cluster. Median, not mean: stable as charges are appended. */
export function medianDayOfMonth(dates: string[]): number {
  return median((dates ?? []).map(d => Number(d.slice(8, 10))));
}

/**
 * Partitions occurrences into billing days — runs of day-of-month numbers no
 * more than BILLING_DAY_DRIFT apart.
 *
 * Needed because a merchant's descriptions can be byte-identical across
 * genuinely different products: every APPLE SERVICES row in the real ledger
 * reads `APPLE.COM/BILL ONE APPLE PARK WAY CUPERTINO`, so productToken has
 * nothing to work with and two separate subscriptions merge into one merchant
 * charged twice a month. Date is the only remaining discriminator.
 *
 * A cluster comes back in DATE order even though clustering runs in
 * day-of-month order, because everything downstream (intervals, first/last,
 * staleness) assumes chronological occurrences.
 *
 * Known limitation: a bill that straddles the month boundary (the 31st one
 * month, the 1st the next) partitions into two fragments rather than one
 * series. Both fragments then fail MIN_DOM_OCCURRENCES, so the merchant stays
 * invisible — exactly as it was before this split ran, since a merchant this
 * split ever sees was already excluded from both the detected list and the
 * "Not detected" candidate list (see BILLING_DAY_DRIFT). The rescue path can
 * only add series, never remove one.
 */
export function splitByBillingDay(occurrences: DailyOccurrence[]): DailyOccurrence[][] {
  const dayOf = (o: DailyOccurrence) => Number(o.date.slice(8, 10));
  const byDay = [...(occurrences ?? [])].sort((a, b) => dayOf(a) - dayOf(b));

  const clusters: DailyOccurrence[][] = [];
  let current: DailyOccurrence[] = [];

  byDay.forEach(o => {
    const previous = current[current.length - 1];
    if (previous && dayOf(o) - dayOf(previous) > BILLING_DAY_DRIFT) {
      clusters.push(current);
      current = [];
    }
    current.push(o);
  });
  if (current.length) clusters.push(current);

  return clusters.map(c => [...c].sort((a, b) => a.date.localeCompare(b.date)));
}

export function occurrencesPerMonth(dates: string[]): number {
  if (!dates || dates.length === 0) return 0;
  const months = new Set(dates.map(d => d.slice(0, 7))).size;
  return months === 0 ? 0 : dates.length / months;
}

/** A bill silent for this many monthly cycles is not a live monthly series.
 *  The same idea isSeriesStale applies to the trailing gap, applied to interior gaps. */
export const MAX_GAP_CADENCES = 3;

/**
 * False when any gap between consecutive (sorted) dates runs longer than
 * MAX_GAP_CADENCES monthly cadences. Needed alongside dayOfMonthSigma: a
 * handful of charges whose day-of-month numbers are all small (e.g. the 1st,
 * 2nd, and 9th) can have a low sigma purely by coincidence even when the real
 * gaps between them are many months apart — a 4-charge series with a single
 * multi-month hole that intervalsAreRegular correctly rejects but that
 * dayOfMonthSigma alone would wave through.
 *
 * Unlike the span-factor guard this replaces, the threshold states a fact
 * about billing behaviour rather than a fact about this ledger: a bill that
 * has gone silent for three monthly cycles (90 days) has stopped, not merely
 * skipped a low-usage month. That gives it margin on both sides — the real
 * gas bill's worst gap is 62 days, comfortably under, while a series with a
 * multi-month hole runs well over.
 */
export function hasNoLongGap(dates: string[]): boolean {
  if (!dates || dates.length < 2) return true;
  const sorted = [...dates].sort();
  const limit = MAX_GAP_CADENCES * CADENCE_DAYS.monthly;
  for (let i = 1; i < sorted.length; i++) {
    if (daysBetween(sorted[i - 1], sorted[i]) > limit) return false;
  }
  return true;
}

/**
 * A second detection path, for bills that are regular by DATE rather than by
 * gap. A utility whose billing cycle shifts, or that skips a month, fails the
 * interval test while still landing on the same day every time — that is the
 * shape of the real water and gas bills, both of which this catches.
 */
export function looksMonthlyByDay(dates: string[]): boolean {
  if (!dates || dates.length < MIN_DOM_OCCURRENCES) return false;
  if (new Set(dates.map(d => d.slice(0, 7))).size < MIN_DOM_OCCURRENCES) return false;
  if (occurrencesPerMonth(dates) > MAX_PER_MONTH) return false;
  if (dayOfMonthSigma(dates) > DOM_SIGMA_MAX) return false;
  return hasNoLongGap(dates);
}

/**
 * Inclusive count of calendar months from the first date to the last —
 * Jan through Aug is 8, not 7. Used to cost a day-of-month series by its
 * observed charge frequency rather than by an assumed twelve-a-year cadence.
 */
export function calendarMonthsSpanned(dates: string[]): number {
  if (!dates || dates.length === 0) return 0;
  const sorted = [...dates].sort();
  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  const months = (Number(last.slice(0, 4)) - Number(first.slice(0, 4))) * 12
    + (Number(last.slice(5, 7)) - Number(first.slice(5, 7)));
  return months + 1;
}

/**
 * Assembles a series from occurrences already judged to qualify. Gating lives
 * in the callers; this only computes. Shared so the whole-group path and the
 * billing-day rescue path can never cost the same shape two different ways.
 */
function buildSeries(
  key: string, merchant: string, collapsed: DailyOccurrence[],
  cadence: Cadence, detectedBy: 'interval' | 'day-of-month',
  asOf: string, billingDay?: number,
): RecurringSeries {
  const amounts = collapsed.map(r => Math.abs(r.amount));
  const dates = collapsed.map(r => r.date);
  const medianCents = median(amounts);
  const amountShape: AmountShape = amountsAreStable(amounts) ? 'fixed' : 'variable';
  const lastDate = dates[dates.length - 1];

  return {
    key, merchant,
    direction: key.startsWith('income:') ? 'income' : 'expense',
    cadence,
    medianCents,
    amountShape,
    minCents: Math.min(...amounts),
    maxCents: Math.max(...amounts),
    // The interval path knows its true period, so monthlyEquivalent converts
    // that period's charge to a monthly rate directly. The day-of-month path
    // has no such period (that is why it fell through to it), so cost it from
    // what was actually observed: a fixed series by median times occurrences
    // over the calendar span, a variable one by the true sum over that span —
    // the median sits inside whichever cluster has more months and understates
    // a seasonal bill by 30-40%.
    monthlyEquivalentCents: detectedBy === 'day-of-month'
      ? Math.round(
          (amountShape === 'fixed'
            ? medianCents * collapsed.length
            : amounts.reduce((a, b) => a + b, 0)) / calendarMonthsSpanned(dates),
        )
      : monthlyEquivalent(medianCents, cadence),
    occurrences: collapsed.length,
    firstDate: dates[0],
    lastDate,
    nextExpected: addDays(lastDate, CADENCE_DAYS[cadence]),
    isStale: isSeriesStale(lastDate, cadence, asOf),
    detectedBy,
    billingDay,
  };
}

/** The whole-group verdict: regular by gap, else regular by calendar day, else nothing. */
function seriesFor(
  key: string, merchant: string, collapsed: DailyOccurrence[], asOf: string,
): RecurringSeries | null {
  const intervals: number[] = [];
  for (let i = 1; i < collapsed.length; i++) {
    intervals.push(daysBetween(collapsed[i - 1].date, collapsed[i].date));
  }
  const dates = collapsed.map(r => r.date);

  const cadence = detectCadence(median(intervals));
  if (cadence && intervalsAreRegular(intervals, cadence)) {
    return buildSeries(key, merchant, collapsed, cadence, 'interval', asOf);
  }
  if (looksMonthlyByDay(dates)) {
    return buildSeries(key, merchant, collapsed, 'monthly', 'day-of-month', asOf);
  }
  return null;
}

/**
 * Recurring series across the ledger, biggest monthly cost first.
 *
 * Transfers and excluded rows never count. Series are keyed by merchant AND
 * direction, so a recurring paycheck is reported as income rather than being
 * summed into the fixed monthly nut.
 *
 * `asOf` is required (an ISO date) so this stays pure and testable — callers
 * supply "today"; staleness classification happens here, but nothing here
 * drops a stale series. That policy call belongs to the callers (see
 * visibleSeries/staleSeries in recurring.calc.ts).
 */
export function detectRecurring(txns: Txn[], asOf: string): RecurringSeries[] {
  const groups = new Map<string, Txn[]>();

  spendableTxns(txns).forEach(t => {
    if (t.amount === 0) return;
    const direction: Direction = t.amount < 0 ? 'expense' : 'income';
    const key = recurringKey(t.matchKey, direction, t.description);
    const bucket = groups.get(key);
    if (bucket) { bucket.push(t); } else { groups.set(key, [t]); }
  });

  const series: RecurringSeries[] = [];

  groups.forEach((rows, key) => {
    // Same-date charges are one occurrence for cadence, count, and amount purposes.
    const collapsed = collapseSameDay(rows);
    if (collapsed.length < MIN_OCCURRENCES) return;
    if (new Set(collapsed.map(r => monthKey(r.date))).size < MIN_DISTINCT_MONTHS) return;

    const merchant = merchantFromKey(key);
    const whole = seriesFor(key, merchant, collapsed, asOf);
    if (whole) { series.push(whole); return; }

    // Rescue path. Only for a merchant rejected for arriving too often: when
    // one merchant name covers two subscriptions on two different billing
    // days, the merged group looks like a shop rather than a bill. A merchant
    // rejected on this condition (occurrencesPerMonth > MAX_PER_MONTH) is
    // already invisible today — recurringCandidates rejects on the identical
    // condition, so it is absent from the "Not detected" list too, not merely
    // from detection. Whatever a partition below fails to qualify simply
    // stays invisible, exactly as it was before this path existed — see
    // BILLING_DAY_DRIFT for why that makes the tight value the safe one.
    if (occurrencesPerMonth(collapsed.map(r => r.date)) <= MAX_PER_MONTH) return;

    splitByBillingDay(collapsed).forEach(cluster => {
      const dates = cluster.map(r => r.date);
      if (!looksMonthlyByDay(dates)) return;
      // Amount stability is load-bearing here in a way it is not on the
      // unsplit paths, where it is only a classification. A partition is
      // built from dates alone, so on its own it happily promotes a shop
      // visited on a habitual day: without this gate the real ledger admits
      // COSTCO WHSE at $101.90/mo and LOWE'S at $159.42/mo. A subscription
      // charges the same amount every time; a shopping trip does not.
      if (!amountsAreStable(cluster.map(r => Math.abs(r.amount)))) return;

      const day = medianDayOfMonth(dates);
      series.push(buildSeries(
        `${key}#${day}`, `${merchant} (${ordinal(day)})`,
        cluster, 'monthly', 'day-of-month', asOf, day,
      ));
    });
  });

  return series.sort((a, b) => b.monthlyEquivalentCents - a.monthlyEquivalentCents);
}
